// Action Schema V1 kind-major codec (FROZEN — 19×19 canvas):
//
//   p = 19*Y + X        (canvas coordinates)
//   a = 361*k + p       k: 0 NORMAL, 1 IMMORTAL, 2 DOUBLE_START, 3 EIGHTWAY
//   PASS = 1444
//
// Smaller boards center on the canvas: 9×9 → offset (5,5), 13×13 → (3,3),
// 19×19 → (0,0). This module is a pure codec: it converts between wire
// actionIds and canvas points. Whether an action is LEGAL is never decided
// here — that is the C++ authority's alone (the legal mask read model is
// the only availability signal the UI may show).

export const CANVAS_SIZE = 19;
export const PASS_ACTION_ID = 1444;
export const ACTION_ID_COUNT = 1445;

export const ACTION_KINDS = ["NORMAL", "IMMORTAL", "DOUBLE_START", "EIGHTWAY"] as const;
export type ActionKind = (typeof ACTION_KINDS)[number];

const KIND_BASE: Record<ActionKind, number> = {
  NORMAL: 0,
  IMMORTAL: 361,
  DOUBLE_START: 722,
  EIGHTWAY: 1083,
};

export function kindOf(actionId: number): ActionKind | "PASS" {
  if (!Number.isSafeInteger(actionId)) throw new RangeError(`actionId is not a safe integer: ${actionId}`);
  if (actionId === PASS_ACTION_ID) return "PASS";
  const k = Math.floor(actionId / 361);
  const kind = ACTION_KINDS[k];
  if (actionId < 0 || actionId > PASS_ACTION_ID || kind === undefined)
    throw new RangeError(`actionId out of range: ${actionId}`);
  return kind;
}

// Canvas point for a point actionId (PASS has no point → null).
export function canvasPointOf(actionId: number): { x: number; y: number } | null {
  if (actionId === PASS_ACTION_ID) return null;
  if (!Number.isSafeInteger(actionId) || actionId < 0 || actionId >= PASS_ACTION_ID)
    throw new RangeError(`actionId out of range: ${actionId}`);
  const p = actionId % 361;
  return { x: p % 19, y: Math.floor(p / 19) };
}

// Action id for a kind + CANVAS point. Pass `pass` for PASS.
export function actionIdOf(kind: ActionKind, canvasX: number, canvasY: number): number {
  if (
    !Number.isSafeInteger(canvasX) || !Number.isSafeInteger(canvasY) ||
    canvasX < 0 || canvasX >= CANVAS_SIZE || canvasY < 0 || canvasY >= CANVAS_SIZE
  )
    throw new RangeError(`canvas point out of range: (${canvasX}, ${canvasY})`);
  return KIND_BASE[kind] + 19 * canvasY + canvasX;
}

// Centering offset of a smaller board on the 19×19 canvas (frozen: 9→5,
// 13→3, 19→0).
export function boardOffset(boardSize: 9 | 13 | 19): number {
  return (CANVAS_SIZE - boardSize) / 2;
}

// Game-board point → canvas point.
export function toCanvas(boardSize: 9 | 13 | 19, x: number, y: number): { x: number; y: number } {
  const off = boardOffset(boardSize);
  return { x: x + off, y: y + off };
}

// Canvas point → game-board point, or null when outside the footprint
// (acting there is illegal; the mask will say so — this is display math).
export function fromCanvas(
  boardSize: 9 | 13 | 19, canvasX: number, canvasY: number,
): { x: number; y: number } | null {
  const off = boardOffset(boardSize);
  const x = canvasX - off;
  const y = canvasY - off;
  if (x < 0 || x >= boardSize || y < 0 || y >= boardSize) return null;
  return { x, y };
}

// Cell index of a game-board point in the exact-state `cells` array
// (row-major within the board's own footprint).
export function cellIndex(boardSize: 9 | 13 | 19, x: number, y: number): number {
  return y * boardSize + x;
}
